import { handleSchema } from "@microbeta/common-types";
import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  RadioGroup,
  RadioGroupItem,
  Text,
  ThemeToggle,
} from "@microbeta/ui";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MetaTags } from "../../../../shared/components";
import { LanguageSelector } from "../../../../shared/components/ui/LanguageSelector/LanguageSelector";
import { useAnalytics } from "../../../../shared/hooks";
import { paths } from "../../../../shared/utils/routing";
import { useAuthContext } from "../../AuthContext";
import background from "../../assets/images/background.svg";
import {
  useCheckHandle,
  useCreateUserProfile,
  useUserProfile,
} from "../../hooks/useUserProfile";
import { generateClimbingHandle } from "./generateClimbingHandle";
import styles from "./onboarding-page.module.scss";

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { authState, logout } = useAuthContext();
  const { captureEvent, trackPageView } = useAnalytics();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirectUrl");
  console.log(`OnboardingPage: redirectUrl from params: ${redirectUrl}`);

  useEffect(() => {
    trackPageView("onboarding_page");
  }, [trackPageView]);
  const [handle, setHandle] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [gradePreference, setGradePreference] = useState<"v-scale" | "font">(
    "v-scale"
  );
  const [unitPreference, setUnitPreference] = useState<"metric" | "imperial">(
    "metric"
  );
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // React Query hooks
  const {
    data: existingProfile,
    isLoading: isCheckingProfile,
    error: profileError,
  } = useUserProfile();
  const {
    data: handleCheck,
    isLoading: isCheckingHandle,
    error: handleCheckError,
  } = useCheckHandle(handle);
  const createProfileMutation = useCreateUserProfile();

  // Redirect unauthenticated users to sign in
  useEffect(() => {
    if (!authState?.idToken) {
      navigate(paths.signin);
    }
  }, [authState?.idToken, navigate]);

  // Redirect if profile exists
  useEffect(() => {
    if (existingProfile) {
      navigate(paths.home);
    }
  }, [existingProfile, navigate]);

  // Gracefully handle profile check errors
  useEffect(() => {
    if (profileError) {
      console.warn(
        "Profile check failed, allowing onboarding to proceed.",
        profileError
      );
    }
  }, [profileError]);

  // Validate handle format
  useEffect(() => {
    if (!handle) {
      setValidationError("");
      return;
    }
    const validation = handleSchema.safeParse(handle);
    setValidationError(
      validation.success
        ? ""
        : validation.error.errors?.[0]?.message || "Invalid handle"
    );
  }, [handle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !handle ||
      !handleCheck?.available ||
      !authState?.idToken ||
      !agreedToTerms
    )
      return;

    try {
      await createProfileMutation.mutateAsync({
        handle,
        display_name: authState.displayName || undefined,
        grade_preference: gradePreference,
        unit_preference: unitPreference,
      });

      captureEvent("profile_completed", {
        handle: handle,
        signin_method: authState.signInMethod || "unknown",
      });

      console.log(
        `OnboardingPage: profile created, navigating to: ${
          redirectUrl || paths.home
        }`
      );
      navigate(redirectUrl || paths.home);
    } catch (error) {
      console.error("Error creating user profile:", error);
    }
  };

  const generateSuggestion = async () => {
    setIsGenerating(true);
    await new Promise((resolve) => setTimeout(resolve, 300));
    setHandle(generateClimbingHandle());
    setIsGenerating(false);
  };

  const getInputStatus = () => {
    if (validationError) return "error";
    if (isCheckingHandle) return "loading";
    if (handleCheckError) return "default";
    if (handleCheck?.available === true) return "success";
    if (handleCheck?.available === false) return "error";
    return "default";
  };

  const getStatusMessage = () => {
    if (validationError) return validationError;
    if (isCheckingHandle) return t("onboarding.checkingAvailability");
    if (handleCheckError) return t("onboarding.handleCheckUnavailable");
    if (handleCheck?.available === true) return t("onboarding.handleAvailable");
    if (handleCheck?.available === false) return t("onboarding.handleTaken");
    return "";
  };

  if (isCheckingProfile) {
    return (
      <div className={styles.container}>
        <img
          src={background}
          alt="background"
          className={styles.backgroundImg}
        />
        <div className={styles.pageWrapper}>
          <LoadingSpinner useRandomPhrase t={t} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <img src={background} alt="background" className={styles.backgroundImg} />
      <div className={styles.themeToggleContainer}>
        <LanguageSelector />
        <ThemeToggle size="sm" />
        <Button
          variant="ghost"
          size="small"
          onClick={logout}
          className={`${styles.controlButton} ${styles.signOutButton}`}
        >
          {t("rootLayout.logout")}
        </Button>
      </div>
      <div className={styles.pageWrapper}>
        <MetaTags
          title="Choose Your Climbing Handle - Microbeta"
          description="Complete your Microbeta account setup by choosing your unique climbing handle."
          noIndex
          noFollow
        />
        <div className={styles.header}>
          <Heading variant="large" className={styles.title}>
            {t("onboarding.chooseHandle")}
          </Heading>
          <Text variant="medium" className={styles.subtitle}>
            {t("onboarding.handleDescription")}
          </Text>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Handle Input Section */}
          <div className={styles.inputContainer}>
            <div
              className={`${styles.inputWrapper} ${
                styles[`status-${getInputStatus()}`]
              }`}
            >
              <span className={styles.atSymbol}>@</span>
              <Input
                type="text"
                placeholder={t("onboarding.handlePlaceholder")}
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                disabled={createProfileMutation.isPending}
                className={`${styles.handleInput} ${
                  styles[`status-${getInputStatus()}`]
                }`}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={generateSuggestion}
              disabled={isGenerating}
              className={styles.suggestButton}
            >
              {isGenerating
                ? t("onboarding.generatingButton")
                : t("onboarding.suggestButton")}
            </Button>
          </div>

          {/* Feedback Section */}
          {(getStatusMessage() || handle.length > 0) && (
            <div className={styles.feedbackContainer}>
              {getStatusMessage() && (
                <div
                  className={`${styles.statusMessage} ${
                    styles[getInputStatus()]
                  }`}
                >
                  <span>{getStatusMessage()}</span>
                </div>
              )}
            </div>
          )}

          {/* Preferences Section */}
          <div className={styles.preferencesSection}>
            <div className={styles.fieldGroup}>
              <Text as="h3" variant="medium" className={styles.fieldLabel}>
                {t("onboarding.gradePreferenceLabel")}
              </Text>
              <RadioGroup
                value={gradePreference}
                onValueChange={(value: "v-scale" | "font") =>
                  setGradePreference(value)
                }
                orientation="horizontal"
                className={styles.radioGroup}
              >
                <RadioGroupItem
                  value="v-scale"
                  label={t("onboarding.vScaleLabel")}
                />
                <RadioGroupItem
                  value="font"
                  label={t("onboarding.fontLabel")}
                />
              </RadioGroup>
            </div>
            <div className={styles.fieldGroup}>
              <Text as="h3" variant="medium" className={styles.fieldLabel}>
                {t("onboarding.unitPreferenceLabel")}
              </Text>
              <RadioGroup
                value={unitPreference}
                onValueChange={(value: "metric" | "imperial") =>
                  setUnitPreference(value)
                }
                orientation="horizontal"
                className={styles.radioGroup}
              >
                <RadioGroupItem
                  value="metric"
                  label={t("onboarding.metricLabel")}
                />
                <RadioGroupItem
                  value="imperial"
                  label={t("onboarding.imperialLabel")}
                />
              </RadioGroup>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className={styles.agreement}>
            <input
              type="checkbox"
              id="agreement"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <label htmlFor="agreement">
              <Text variant="small">
                I have read and agree to the{" "}
                <Link
                  to={paths.legal.index}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and Conditions
                </Link>
              </Text>
            </label>
          </div>

          {createProfileMutation.isError && (
            <div className={styles.errorMessage}>
              {createProfileMutation.error?.message ||
                t("onboarding.profileCreationFailed")}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={
              !handle ||
              handleCheck?.available === false ||
              createProfileMutation.isPending ||
              isCheckingHandle ||
              !!validationError ||
              !agreedToTerms
            }
            className={styles.submitButton}
          >
            {createProfileMutation.isPending
              ? t("onboarding.creatingProfileButton")
              : t("onboarding.createProfileButton")}
          </Button>
        </form>
      </div>
    </div>
  );
};
